import { useCallback, useEffect, useMemo, useState } from "react";
import { FormValidation, IChangeEvent } from "react-jsonschema-form";
import { matchPath, useLocation, useRouteMatch } from "react-router";
import { useAccommodationCategoryLimitsQuery } from "../../../extrasCategories/application/hooks/useAccommodationCategoryLimitsQuery";
import { useIssueFormatter } from "../../../extrasCategories/application/hooks/useIssueFormatter";
import { ValidationIssue } from "../../../extrasCategories/domain/entities/ValidationIssue";
import { GrpcIssues } from "../../../extrasCategories/infrastructure/grpcIssues";
import { addCategoryLimitsErrors } from "../../../extrasCategories/presentation/components/CategoryLimitsField";
import { useSaveWarningsDialog } from "../../../extrasCategories/presentation/components/SaveWarningsDialog";
import { useCategoryLimitsState } from "../../../extrasCategories/presentation/context/useCategoryLimitsState";
import { mapCategoryLimitsToFormData } from "../../../extrasCategories/presentation/mappers/category-limits.mapper";
import { AccommodationEntity, AccommodationForm } from "../../domain/Accommodation.entity";
import { asCategoryLimitsGrpc } from "../mappers/grpc.mapper";
import { schemaWithCategoryLimits, schemaWithoutCategoryLimits } from "../schema";
import { CategoryLimitsFormOptions } from "./formConfiguration.hook";

/** Id of the open accommodation form: `""` on create, `undefined` on the list. */
function useOpenAccommodationId(): string | undefined {
  const { url } = useRouteMatch();
  const { pathname } = useLocation();
  if (matchPath(pathname, { path: `${url}/new`, exact: true })) {
    return "";
  }
  return matchPath<{ id: string }>(pathname, { path: `${url}/edit/:id`, exact: true })?.params.id;
}

const nameInLocale = (entity: Pick<AccommodationEntity, "translationsList">, locale: string) =>
  entity.translationsList?.find(translation => translation.locale === locale)?.name ?? "";

/**
 * The *Limits per category* section of the accommodation page. Everything is inert while the
 * flag is off or the section is not visible: the page then gets today's schema and handlers.
 */
export function useAccommodationCategoryLimits(locale: string) {
  const accommodationId = useOpenAccommodationId();
  const { data: limits, isLoading } = useAccommodationCategoryLimitsQuery(accommodationId, locale);
  const sectionVisible = limits?.isSectionVisible() === true;

  const [accommodationName, setAccommodationName] = useState("");

  const { value: context, showServerIssues, clearAllServerIssues } = useCategoryLimitsState(
    limits,
    accommodationName
  );

  /** Opening another form, or going back to the list, starts from a clean state. */
  useEffect(() => {
    setAccommodationName("");
    clearAllServerIssues();
  }, [accommodationId, clearAllServerIssues]);

  const issueFormatter = useIssueFormatter();
  const { showWarnings, dialog: warningsDialog } = useSaveWarningsDialog();

  const initialState = useMemo(
    () => (limits && sectionVisible ? mapCategoryLimitsToFormData(limits) : undefined),
    [limits, sectionVisible]
  );

  const schema = useMemo(
    () =>
      limits && sectionVisible
        ? schemaWithCategoryLimits(limits.categories())
        : schemaWithoutCategoryLimits,
    [limits, sectionVisible]
  );

  const formOptions = useMemo<CategoryLimitsFormOptions>(() => ({ sectionVisible, initialState }), [
    sectionVisible,
    initialState,
  ]);

  /**
   * The loaded accommodation with the section's initial state. It is a plain object, like the
   * data rjsf builds: rjsf compares constructors too, and a difference marks the form as changed.
   */
  const withInitialState = useCallback(
    (entity: AccommodationEntity): AccommodationForm => {
      setAccommodationName(nameInLocale(entity, locale));
      return initialState ? { ...entity, categoryLimits: initialState } : entity;
    },
    [initialState, locale]
  );

  /**
   * Tracks the name typed so far, used in the help texts. The return value replaces the page data,
   * so it is always the current form data: an `undefined` would make rjsf reset the form.
   */
  const handleFormChange = useCallback(
    (event: IChangeEvent<AccommodationForm>) => {
      setAccommodationName(nameInLocale(event.formData, locale));
      return event.formData;
    },
    [locale]
  );

  const addValidationErrors = useCallback(
    (formData: AccommodationForm, errors: FormValidation) =>
      addCategoryLimitsErrors(errors, limits, formData.categoryLimits, issue =>
        issueFormatter.format(issue)
      ),
    [limits, issueFormatter]
  );

  /** `category_limits` of the save request, `undefined` while the section is not visible. */
  const requestInput = useCallback(
    (entity: AccommodationForm) => asCategoryLimitsGrpc(limits, entity.categoryLimits),
    [limits]
  );

  /**
   * Server validation errors on a block are shown in it. Returns the text of the rest, for the
   * page alert, or `undefined` when there are none.
   */
  const handleSaveRejected = useCallback(
    (error: unknown): string | undefined => {
      const rejection = GrpcIssues.read(error);
      if (!sectionVisible || !rejection?.isInvalidArgument()) {
        return undefined;
      }
      showServerIssues(rejection.issues);
      const pageIssues = rejection.issues.filter(issue => !issue.target.isBlockScoped());
      return pageIssues.length
        ? pageIssues.map(issue => issueFormatter.format(issue)).join(" ")
        : undefined;
    },
    [sectionVisible, showServerIssues, issueFormatter]
  );

  /** Shows the save warnings after every post-save step and before going back to the list. */
  const handleSaved = useCallback(
    (warnings: readonly ValidationIssue[]) => showWarnings(warnings),
    [showWarnings]
  );

  return {
    isLoading,
    schema,
    formOptions,
    context,
    warningsDialog,
    withInitialState,
    onFormChange: sectionVisible ? handleFormChange : undefined,
    addValidationErrors,
    requestInput,
    onSaveStarted: clearAllServerIssues,
    onSaveRejected: handleSaveRejected,
    onSaved: handleSaved,
  };
}
